import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Filter, LayoutGrid, SearchX, SlidersHorizontal, X } from "lucide-react";
import { SEO } from "../components/SEO";
import { ProductCard } from "../components/ProductCard";
import { Skeleton } from "../components/ui/skeleton";
import { api, track } from "../lib/api";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "name", label: "Name A–Z" },
  { value: "newest", label: "Newest" },
];

export default function ShopPage({ mode = "shop" }) {
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";

  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const page = Number(searchParams.get("page")) || 1;
  const sort = searchParams.get("sort") || "featured";
  const onSale = searchParams.get("on_sale") === "true";
  const inStock = searchParams.get("in_stock") !== "false";
  const minPrice = searchParams.get("min_price") || "";
  const maxPrice = searchParams.get("max_price") || "";
  const [priceDraft, setPriceDraft] = useState({ min: minPrice, max: maxPrice });

  const activeCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(categoryId)),
    [categories, categoryId]
  );

  useEffect(() => {
    api.categories().then((d) => setCategories(d.items || [])).catch(() => {});
  }, []);

  const setParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null || value === "" || value === false) next.delete(key);
      else next.set(key, value);
      if (key !== "page") next.delete("page");
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    setLoading(true);
    const params = {
      page, limit: 24, sort,
      in_stock: inStock ? true : undefined,
      on_sale: onSale ? true : undefined,
      min_price: minPrice || undefined,
      max_price: maxPrice || undefined,
      q: q || undefined,
      category: categoryId || undefined,
    };
    api.products(params)
      .then((d) => {
        setData(d);
        track(mode === "search" ? "search" : "view_item_list", { q, category: categoryId, results: d.total });
      })
      .catch(() => setData({ items: [], total: 0, page: 1, pages: 1 }))
      .finally(() => setLoading(false));
  }, [page, sort, onSale, inStock, minPrice, maxPrice, q, categoryId, mode]);

  const topLevel = categories.filter((c) => !c.parentId);
  const childrenOf = (id) => categories.filter((c) => c.parentId === id);

  const title = mode === "search"
    ? (q ? `Results for "${q}"` : "Search the Menu")
    : activeCategory ? activeCategory.name : "Shop All";

  const breadcrumbs = activeCategory
    ? [{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }, { name: activeCategory.name }]
    : [{ name: "Home", path: "/" }, { name: title }];

  const filterPanel = (
    <div className="space-y-8" data-testid="filter-panel">
      <div>
        <h3 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-brand-golddeep">Categories</h3>
        <ul className="mt-4 space-y-1">
          <li>
            <Link to="/shop" data-testid="filter-category-all" className={`block rounded-lg px-3 py-2 text-sm transition-colors ${!categoryId ? "bg-brand-pine/5 font-bold text-brand-pine" : "text-brand-ink/65 hover:text-brand-pine"}`}>
              All Products
            </Link>
          </li>
          {topLevel.map((c) => (
            <li key={c.id}>
              <Link
                to={`/category/${c.id}`}
                data-testid={`filter-category-${c.slug}`}
                className={`block rounded-lg px-3 py-2 text-sm transition-colors ${String(c.id) === String(categoryId) ? "bg-brand-pine/5 font-bold text-brand-pine" : "text-brand-ink/65 hover:text-brand-pine"}`}
              >
                {c.name} <span className="text-xs text-brand-ink/40">({c.productCount})</span>
              </Link>
              {childrenOf(c.id).length > 0 && (
                <ul className="ml-3 mt-1 space-y-1 border-l border-brand-line pl-3">
                  {childrenOf(c.id).map((s) => (
                    <li key={s.id}>
                      <Link
                        to={`/category/${s.id}`}
                        data-testid={`filter-subcategory-${s.slug}`}
                        className={`block rounded-lg px-2 py-1.5 text-xs transition-colors ${String(s.id) === String(categoryId) ? "font-bold text-brand-pine" : "text-brand-ink/55 hover:text-brand-pine"}`}
                      >
                        {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-brand-golddeep">Price</h3>
        <form
          className="mt-4 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setParam("min_price", priceDraft.min);
            setParam("max_price", priceDraft.max);
          }}
        >
          <input
            type="number" min="0" placeholder="Min" value={priceDraft.min}
            onChange={(e) => setPriceDraft((d) => ({ ...d, min: e.target.value }))}
            data-testid="price-min-input" aria-label="Minimum price"
            className="w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm outline-none focus:border-brand-gold"
          />
          <span className="text-brand-ink/40">–</span>
          <input
            type="number" min="0" placeholder="Max" value={priceDraft.max}
            onChange={(e) => setPriceDraft((d) => ({ ...d, max: e.target.value }))}
            data-testid="price-max-input" aria-label="Maximum price"
            className="w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm outline-none focus:border-brand-gold"
          />
          <button type="submit" data-testid="price-apply-button" className="rounded-lg bg-brand-pine px-3 py-2 text-xs font-bold text-brand-cream">
            Go
          </button>
        </form>
      </div>
      <div className="space-y-3">
        <h3 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-brand-golddeep">Refine</h3>
        <label className="flex cursor-pointer items-center gap-3 text-sm text-brand-ink/70">
          <input
            type="checkbox" checked={onSale} onChange={(e) => setParam("on_sale", e.target.checked ? "true" : null)}
            data-testid="filter-on-sale" className="h-4 w-4 accent-brand-pine"
          />
          On sale only
        </label>
        <label className="flex cursor-pointer items-center gap-3 text-sm text-brand-ink/70">
          <input
            type="checkbox" checked={inStock} onChange={(e) => setParam("in_stock", e.target.checked ? null : "false")}
            data-testid="filter-in-stock" className="h-4 w-4 accent-brand-pine"
          />
          In stock only
        </label>
      </div>
    </div>
  );

  return (
    <div data-testid="shop-page">
      <SEO
        title={title}
        description={activeCategory ? `Shop ${activeCategory.name} at CannaCheap DC — premium quality, honest prices, fast DC delivery.` : undefined}
        path={mode === "search" ? "/search" : activeCategory ? `/category/${activeCategory.id}` : "/shop"}
        jsonLd={{
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem", position: i + 1, name: b.name,
            ...(b.path ? { item: `https://www.cannacheapdc.com${b.path}` } : {}),
          })),
        }}
      />

      <section className="bg-brand-forest py-14 text-brand-cream lg:py-20" data-testid="shop-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <nav aria-label="Breadcrumb" className="text-xs text-brand-cream/50">
            {breadcrumbs.map((b, i) => (
              <span key={i}>
                {i > 0 && <span className="mx-2">/</span>}
                {b.path ? <Link to={b.path} data-testid={`breadcrumb-${i}`} className="hover:text-brand-gold">{b.name}</Link> : <span className="text-brand-cream/85">{b.name}</span>}
              </span>
            ))}
          </nav>
          <p className="mt-10 text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-gold">The Menu</p>
          <h1 className="mt-3 text-balance font-display text-4xl font-black sm:text-5xl lg:text-6xl">
            {mode === "shop" ? "Shop by Category" : title}
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-brand-cream/70">
            Premium flower, concentrates, edibles, pre-rolls and more — delivered fast and discreet across DC.
          </p>
          <a
            href="#product-grid"
            data-testid="view-all-products"
            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-brand-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-forest transition-transform hover:scale-[1.03]"
          >
            <LayoutGrid className="h-4 w-4" /> View All Products
          </a>
        </div>
      </section>

      {mode === "shop" && topLevel.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pt-14 sm:px-6 lg:px-10" data-testid="shop-category-grid">
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-3 lg:gap-x-8">
            {topLevel.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                data-testid={`shop-category-card-${cat.slug}`}
                className="group flex flex-col items-center p-2 text-center sm:p-4"
              >
                <div className="flex h-44 w-full items-center justify-center sm:h-60">
                  {cat.image ? (
                    <img
                      src={cat.image}
                      alt={`${cat.name} category`}
                      loading="lazy"
                      className="img-blend max-h-full w-auto max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <span className="font-display text-4xl font-black text-brand-ink/15">CC</span>
                  )}
                </div>
                <h2 className="mt-4 font-display text-sm font-black uppercase tracking-wide text-brand-forest sm:text-base">{cat.name}</h2>
                <span className="mt-2.5 h-0.5 w-8 rounded-full bg-brand-gold transition-all duration-300 group-hover:w-14" />
                <p className="mt-2 text-xs text-brand-ink/50">{cat.productCount} products</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div id="product-grid" className="mt-12 flex scroll-mt-24 flex-wrap items-end justify-between gap-4">
        <p className="text-sm text-brand-ink/55" data-testid="results-count">
          {loading ? "Loading…" : `${data.total} product${data.total === 1 ? "" : "s"}`}
        </p>
        <div className="flex items-center gap-3">
          <button
            data-testid="filters-toggle"
            onClick={() => setFiltersOpen(true)}
            className="flex items-center gap-2 rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-medium text-brand-forest lg:hidden"
          >
            <SlidersHorizontal className="h-4 w-4" /> Filters
          </button>
          <label className="sr-only" htmlFor="sort-select">Sort products</label>
          <select
            id="sort-select"
            value={sort}
            onChange={(e) => setParam("sort", e.target.value)}
            data-testid="sort-select"
            className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-medium text-brand-forest outline-none focus:border-brand-gold"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-8 grid gap-10 lg:grid-cols-12">
        <aside className="hidden lg:col-span-3 lg:block" aria-label="Product filters">
          {filterPanel}
        </aside>

        <div className="lg:col-span-9">
          {loading ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 lg:gap-x-6" data-testid="products-loading">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i}>
                  <Skeleton className="aspect-[4/5] rounded-2xl" />
                  <Skeleton className="mt-3 h-4 w-3/4" />
                  <Skeleton className="mt-2 h-4 w-1/3" />
                </div>
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-brand-line bg-white py-20 text-center" data-testid="empty-state">
              <SearchX className="h-10 w-10 text-brand-ink/25" />
              <p className="font-display text-xl font-bold text-brand-forest">Nothing found</p>
              <p className="max-w-xs text-sm text-brand-ink/55">Try a different search, clear your filters, or browse the full menu.</p>
              <Link to="/shop" data-testid="empty-state-shop-all" className="rounded-full bg-brand-pine px-6 py-3 text-sm font-bold text-brand-cream">
                Shop All
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 lg:gap-x-6" data-testid="product-grid">
              {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}

          {data.pages > 1 && !loading && (
            <nav className="mt-14 flex items-center justify-center gap-2" aria-label="Pagination" data-testid="pagination">
              <button
                data-testid="pagination-prev"
                disabled={page <= 1}
                onClick={() => setParam("page", String(page - 1))}
                className="rounded-full border border-brand-line p-2.5 text-brand-forest disabled:opacity-30"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: data.pages }).map((_, i) => (
                <button
                  key={i}
                  data-testid={`pagination-page-${i + 1}`}
                  onClick={() => setParam("page", String(i + 1))}
                  aria-current={page === i + 1 ? "page" : undefined}
                  className={`h-10 w-10 rounded-full text-sm font-bold transition-colors ${
                    page === i + 1 ? "bg-brand-pine text-brand-cream" : "border border-brand-line text-brand-ink/60 hover:border-brand-pine"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                data-testid="pagination-next"
                disabled={page >= data.pages}
                onClick={() => setParam("page", String(page + 1))}
                className="rounded-full border border-brand-line p-2.5 text-brand-forest disabled:opacity-30"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </nav>
          )}
        </div>
      </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true" aria-label="Filters" data-testid="mobile-filters">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFiltersOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-3xl bg-brand-cream p-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-lg font-black text-brand-forest">
                <Filter className="h-5 w-5" /> Filters
              </h2>
              <button data-testid="mobile-filters-close" aria-label="Close filters" onClick={() => setFiltersOpen(false)} className="p-2">
                <X className="h-5 w-5" />
              </button>
            </div>
            {filterPanel}
            <button
              data-testid="mobile-filters-apply"
              onClick={() => setFiltersOpen(false)}
              className="mt-8 w-full rounded-full bg-brand-pine py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-cream"
            >
              Show {data.total} Results
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
